import { useState } from 'react';
import { Alert, Button, PasswordInput, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { errorMessage } from '@/api/http';
import { useAuth } from '@/auth/context';
import { AuthCard } from './AuthCard';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    initialValues: { name: '', password: '', confirm: '' },
    validate: {
      name: (v) => (v.trim() ? null : 'Enter your name.'),
      password: (v) => (v.length >= 10 ? null : 'Use at least 10 characters.'),
      confirm: (v, all) => (v === all.password ? null : 'The passwords don’t match.'),
    },
  });

  if (!token) {
    return (
      <AuthCard title="Invite link incomplete">
        <Alert color="coral" variant="light">
          This link is missing its invite token. Open the link from your invite email again, or ask
          an admin to resend it.
        </Alert>
        <Button component={Link} to="/login" variant="default">
          Go to sign in
        </Button>
      </AuthCard>
    );
  }

  const submit = form.onSubmit(async (v) => {
    setError(null);
    try {
      await acceptInvite(token, v.name.trim(), v.password);
      navigate('/programmes', { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  return (
    <AuthCard
      title="Join the Playbox admin"
      subtitle="Choose your name and a password to finish setting up your account."
    >
      <form onSubmit={submit} noValidate>
        <Stack>
          {error && (
            <Alert color="coral" variant="light" role="alert">
              {error}
            </Alert>
          )}
          <TextInput
            label="Your name"
            autoComplete="name"
            required
            {...form.getInputProps('name')}
          />
          <PasswordInput
            label="Password"
            description="At least 10 characters."
            autoComplete="new-password"
            required
            {...form.getInputProps('password')}
          />
          <PasswordInput
            label="Confirm password"
            autoComplete="new-password"
            required
            {...form.getInputProps('confirm')}
          />
          <Button type="submit" loading={form.submitting} fullWidth>
            Create account
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
